import { Link } from "react-router-dom";
import { ArrowLeft, Zap, Users, Compass } from "lucide-react";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import { Reveal, FadeStagger, FadeItem } from "@/components/Motion";

const ACCENT = "#0d9488";
const CASE_ICONS = [Zap, Users, Compass];

const PeopleCase = () => {
  const { t } = useLang();
  const p = t.people;

  return (
    <div data-testid="people-case-page" className="bg-[#FAFAFD] text-[#353535] min-h-screen">
      <Navbar accent={ACCENT} />

      <section className="px-5 sm:px-8 pt-32 pb-12 sm:pt-40">
        <div className="mx-auto max-w-[1200px]">
          <Link to="/people-hr" data-testid="case-back-link" className="group inline-flex items-center gap-2 font-jbmono text-[11px] uppercase tracking-[0.25em] text-[#999999] hover:text-[#353535] transition-colors mb-8">
            <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" /> {t.common.backToPeople}
          </Link>
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#0d9488] mb-4">{p.caseKicker}</div>
            <h1 className="font-playfair text-3xl sm:text-5xl leading-tight max-w-3xl">{p.caseTitle}</h1>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-14 border-t border-black/5">
        <div className="mx-auto max-w-[1200px] grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#999999] mb-3">{p.caseContextLabel}</div>
            <p className="text-sm sm:text-base font-jakarta text-[#353535]/80 leading-relaxed">{p.caseContext}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#999999] mb-3">{p.caseImplLabel}</div>
            <p className="text-sm sm:text-base font-jakarta text-[#353535]/80 leading-relaxed">{p.caseImpl}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-14 border-t border-black/5">
        <div className="mx-auto max-w-[1200px]">
          <Reveal>
            <div className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#999999] mb-6">{p.caseFindingsLabel}</div>
          </Reveal>
          <FadeStagger className="grid gap-4 lg:grid-cols-3">
            {p.caseFindings.map((f, i) => {
              const Icon = CASE_ICONS[i];
              return (
                <FadeItem key={i}>
                  <div data-testid={`case-finding-${i}`} className="h-full rounded-2xl border border-black/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(13,148,136,0.10)]">
                    <div className="flex items-center gap-2 font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#0d9488]">
                      <Icon size={13} /> {f.module}
                    </div>
                    <div className="mt-4 font-cabinet text-4xl font-bold tracking-tight">{f.stat}</div>
                    <p className="mt-3 text-sm font-jakarta text-[#999999] leading-relaxed">{f.text}</p>
                  </div>
                </FadeItem>
              );
            })}
          </FadeStagger>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-3xl border-l-2 border-[#0d9488] pl-5 font-jakarta text-sm italic text-[#353535]/70">{p.caseFindingsNote}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-14 border-t border-black/5">
        <div className="mx-auto max-w-[1200px] grid gap-10 lg:grid-cols-2 items-start">
          <Reveal>
            <div className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#999999] mb-3">{p.caseActionLabel}</div>
            <p className="text-sm sm:text-base font-jakarta text-[#353535]/80 leading-relaxed">{p.caseActionIntro}</p>
            <div className="mt-6 flex flex-col">
              {p.caseActions.map((a, i) => (
                <div key={i} data-testid={`case-action-${i}`} className="flex gap-4 border-t border-black/10 py-4">
                  <span className="shrink-0 pt-0.5 font-jbmono text-xs text-[#0d9488]">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm font-jakarta text-[#353535]/85 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-[#353535] p-8 sm:p-10 text-white">
              <div className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#0d9488]">{p.caseResultsLabel}</div>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                {p.caseResults.map((r, i) => (
                  <div key={i} data-testid={`case-result-${i}`}>
                    <div className="font-cabinet text-3xl sm:text-4xl font-bold text-[#0d9488]">{r.stat}</div>
                    <p className="mt-2 text-[13px] font-jakarta text-white/70 leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-14 border-t border-black/5">
        <Reveal className="mx-auto max-w-[1200px]">
          <p className="max-w-3xl font-playfair text-2xl sm:text-3xl leading-snug">{p.caseClosing}</p>
        </Reveal>
      </section>

      <section className="px-5 sm:px-8 py-20 border-t border-black/10">
        <div className="mx-auto max-w-[1200px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <h2 className="font-playfair text-3xl sm:text-4xl leading-tight">{p.ctaTitle}</h2>
            <p className="mt-4 text-[#999999] font-jakarta max-w-md">{p.ctaBody}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <LeadForm defaultUnit="people" service="caso-exito" accent={ACCENT} />
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PeopleCase;
